import api from "@/api/api";
import showConfirm from "@/components/utils/ui_v4/alerts/confirm/confirm";
import { toast } from "@/components/utils/ui_v4/alerts/toast/toast";
import { computed, ref, watch } from "vue";
import {
  getDecisaoColor,
  getStatusColor,
  getStatusIcon,
  getSituacaoColor
} from "../constants";
import { parseDate } from "../utils/dateUtils";

export default function useDetalhesCotacaoDialog(props) {
  const isLoading = ref(false);
  const cotacao = ref(null);
  const produtos = ref([]);
  const resumo = ref({});
  const fornecedores = ref([]);
  const responsavel = ref({});
  const config = ref({});
  const seguidores = ref([]);
  const empresa = ref({});
  const orders = ref([]);
  const pedidos = ref([]);


  const baixarEvidencias = async () => {
    if (!props.idCotacao) {

      return;
    }

    try {
      isLoading.value = true;

      // Fazer requisição para o endpoint que retorna o PDF
      const response = await api.get(
        `/cotacao/baixar-evidencias/${props.idCotacao}`,
        {
          responseType: 'blob',
        }
      );

      const contentType = response.headers['content-type'] || '';
      const isPDF = contentType.includes('application/pdf');
      const isHTML = contentType.includes('text/html');

      // Extrair nome do arquivo do header Content-Disposition ou usar um padrão
      const contentDisposition = response.headers['content-disposition'] || '';
      let fileName = `evidencias_cotacao_${props.idCotacao}_${new Date().getTime()}.${isPDF ? 'pdf' : 'html'}`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      if (isPDF) {
        // Se for PDF, fazer download direto
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else if (isHTML) {
        const htmlContent = typeof response.data === 'string'
          ? response.data
          : await new Blob([response.data]).text();

        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback: tentar fazer download como está
        const blob = new Blob([response.data], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {

      // Se o erro for JSON (erro do backend), tentar mostrar a mensagem
      if (error.response && error.response.data) {
        try {
          // Se for blob, converter para texto primeiro
          if (error.response.data instanceof Blob) {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            if (errorData.message) {
              alert(`Erro ao baixar evidências: ${errorData.message}`);
            } else {
              alert('Erro ao baixar evidências. Tente novamente.');
            }
          } else if (typeof error.response.data === 'object' && error.response.data.message) {
            alert(`Erro ao baixar evidências: ${error.response.data.message}`);
          } else {
            alert('Erro ao baixar evidências. Tente novamente.');
          }
        } catch (e) {
          alert('Erro ao baixar evidências. Tente novamente.');
        }
      } else {
        alert('Erro ao baixar evidências. Tente novamente.');
      }
    } finally {
      isLoading.value = false;
    }
  }

  const close = () => {
    if (props.handleClose) {
      props.handleClose();
    }
  }

  const edit = () => {
    // Abrir o dialog de edição via callback PRIMEIRO
    if (props.handleEdit) {
      props.handleEdit(props.idCotacao);
    }

    // Depois fechar o dialog atual (sem recarregar)
    // O handleClose será chamado pelo watch do dialogVisible
  }

  const repeatQuotation = async () => {
    if (!props.idCotacao) {

      return;
    }

    const msgSemPedido = {
      title: "Não é possível repetir a cotação",
      text:
        "Não é possível repetir a cotação porque não existe pedido vinculado a esta cotação.",
    };

    try {
      isLoading.value = true;

      try {
        const { data: listaPedidos } = await api.get(
          `/cotacao/listar_pedidos_cotacao/${props.idCotacao}`
        );
        if (
          !listaPedidos.success ||
          !listaPedidos.total_pedidos ||
          listaPedidos.total_pedidos < 1
        ) {
          toast.error(msgSemPedido.title, msgSemPedido.text);

          return;
        }
      } catch (errLista) {
        const status = errLista.response && errLista.response.status;
        if (status === 404) {
          toast.error(msgSemPedido.title, msgSemPedido.text);

          return;
        }
        const m =
          errLista.response &&
          errLista.response.data &&
          errLista.response.data.message
            ? errLista.response.data.message
            : "Não foi possível verificar os pedidos desta cotação.";
        toast.error("Erro ao verificar pedidos", m);

        return;
      }

      const { data } = await api.post(`/cotacao/repetir_cotacao/${props.idCotacao}`);

      if (data.success) {
        const novaId =
          data.cotacao_transacao_id_nova != null
            ? data.cotacao_transacao_id_nova
            : data.cotacao_transacao_id;

        toast.success(
          "Cotação repetida",
          data.message || "Nova cotação criada com base nesta."
        );

        // Fechar o dialog atual
        if (props.handleClose) {
          props.handleClose();
        }

        // Abrir o dialog de edição com a nova cotação criada
        if (props.handleEdit && novaId) {
          props.handleEdit(novaId);
        }
      } else {
        toast.error(
          data.error || "Não foi possível repetir",
          data.message || "Tente novamente."
        );
      }
    } catch (error) {
      const title =
        (error.response && error.response.data && error.response.data.error) ||
        "Erro ao repetir cotação";
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        "Não foi possível repetir a cotação. Tente novamente.";
      toast.error(title, message);
    } finally {
      isLoading.value = false;
    }
  };

  const endQuotationNow = async () => {
    if (!props.idCotacao) {

      return;
    }

    showConfirm({
      title: 'Encerrar cotação',
      message: 'Tem certeza que deseja encerrar o prazo desta cotação agora? Esta ação não pode ser desfeita.',
      button: {
        yes: 'Confirmar',
        no: 'Cancelar'
      },
      callback: async (confirmed) => {
        if (confirmed) {
          try {
            const { data } = await api.post(`/cotacao/encerrar_prazo/${props.idCotacao}`);

            if (data.success) {
              // Exibir mensagem de sucesso
              const successMessage = data.message || 'Prazo da cotação encerrado com sucesso';
              toast.success('Sucesso', successMessage);

              // Recarregar os detalhes da cotação
              await fetchDetails();
            } else {
              // Exibir mensagem de erro
              const errorMessage = data.message || 'Não foi possível encerrar o prazo da cotação.';
              const errorTitle = data.error || 'Erro ao encerrar prazo!';
              toast.error(errorTitle, errorMessage);
            }
          } catch (error) {
            // Extrair mensagem do erro do backend se disponível
            let errorMessage = 'Não foi possível encerrar o prazo da cotação. Tente novamente.';
            let errorTitle = 'Erro ao encerrar prazo!';

            if (error.response && error.response.data) {
              errorTitle = error.response.data.error || errorTitle;
              errorMessage = error.response.data.message || errorMessage;
            }

            toast.error(errorTitle, errorMessage);
          }
        }
      }
    });
  }

  // Estado do modal de prorrogação
  const extendDeadlineDialog = ref(false);
  const newDeadlineDate = ref('');
  const newDeadlineTime = ref('');

  const openExtendDeadlineDialog = () => {
    // Inicializar com a data atual de término se existir
    if (config.value && config.value.termino_periodo_recebimento_proposta) {
      const terminoData = config.value.termino_periodo_recebimento_proposta;
      let currentDeadline = null;

      // Se já é um objeto Date válido
      if (terminoData instanceof Date && !isNaN(terminoData.getTime())) {
        currentDeadline = terminoData;
      } else if (typeof terminoData === 'string' && terminoData.trim() !== '') {
        // Tentar diferentes formatos de parsing
        const dateStr = terminoData.trim();

        // Formato 1: ISO 8601 (YYYY-MM-DDTHH:mm:ss ou YYYY-MM-DDTHH:mm:ss.sssZ)
        currentDeadline = new Date(dateStr);
        if (!isNaN(currentDeadline.getTime())) {
          // Data válida, usar ela
        } else {
          // Formato 2: PostgreSQL timestamp (YYYY-MM-DD HH:mm:ss ou YYYY-MM-DD HH:mm:ss.uuuuuu)
          const cleanedDate = dateStr.replace(/\.\d+$/, ''); // Remove microsegundos
          const isoDate = cleanedDate.replace(' ', 'T');
          currentDeadline = new Date(isoDate);

          if (isNaN(currentDeadline.getTime())) {
            // Formato 3: Tentar parse direto com Date.parse
            const parsed = Date.parse(dateStr);
            if (!isNaN(parsed)) {
              currentDeadline = new Date(parsed);
            } else {
              currentDeadline = null;
            }
          }
        }
      }

      // Se conseguiu criar uma data válida, usar ela
      if (currentDeadline && !isNaN(currentDeadline.getTime())) {
        newDeadlineDate.value = currentDeadline.toISOString().split('T')[0];
        newDeadlineTime.value = currentDeadline.toTimeString().slice(0, 5);
      } else {
        // Se não conseguiu, usar padrão (sem throw para evitar warning)
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        newDeadlineDate.value = tomorrow.toISOString().split('T')[0];
        newDeadlineTime.value = '23:59';
      }
    } else {
      // Padrão: amanhã às 23:59
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      newDeadlineDate.value = tomorrow.toISOString().split('T')[0];
      newDeadlineTime.value = '23:59';
    }
    extendDeadlineDialog.value = true;
  };

  const closeExtendDeadlineDialog = () => {
    extendDeadlineDialog.value = false;
    newDeadlineDate.value = '';
    newDeadlineTime.value = '';
  };

  const gerarPedidos = async () => {
    if (!props.idCotacao) {

      return;
    }

    try {
      isLoading.value = true;

      const { data } = await api.post(`/cotacao/gerar_pedidos_fornecedores_ganhadores/${props.idCotacao}`);

      if (data.success) {
        toast.success('Pedidos gerados com sucesso!', data.message || 'Os pedidos foram criados para os fornecedores ganhadores.');

        // Recarregar os detalhes da cotação
        await fetchDetails();

        // Ir para a aba de pedidos (index 2)
        activeTab.value = 2;
      } else {
        const errorMessage = data.message || 'Não foi possível gerar os pedidos.';
        const errorTitle = data.error || 'Erro ao gerar pedidos!';
        toast.error(errorTitle, errorMessage);
      }
    } catch (error) {
      let errorMessage = 'Não foi possível gerar os pedidos. Tente novamente.';
      let errorTitle = 'Erro ao gerar pedidos!';

      if (error.response && error.response.data) {
        errorTitle = error.response.data.error || errorTitle;
        errorMessage = error.response.data.message || errorMessage;
      }

      toast.error(errorTitle, errorMessage);
    } finally {
      await fetchPedidos();
      await fetchDetails();
      isLoading.value = false;
    }
  };

  const baixarPdfPedido = async (idPedido) => {
    if (!props.idCotacao || !idPedido) {
      toast.error('ID da cotação ou pedido não encontrado');
      return;
    }

    if (!cotacao.value || cotacao.value.situacao_pedidos_nome !== 'Todos gerados') {
      toast.warning('É necessário gerar todos os pedidos antes de baixá-los.');
      return;
    }

    try {
      isLoading.value = true;

      const response = await api.get(
        `/cotacao/baixar-pdf-pedidos/${props.idCotacao}/${idPedido}`,
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pedido_${idPedido}_cotacao_${props.idCotacao}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {

      let errorMessage = 'Erro ao baixar PDF do pedido. Tente novamente.';

      if (error.response && error.response.data) {
        if (error.response.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            const errorData = JSON.parse(text);
            if (errorData.message) errorMessage = errorData.message;
          } catch (e) {
            // Ignora erro de parse
          }
        }
      }
      toast.error('Erro', errorMessage);
    } finally {
      isLoading.value = false;
    }
  };

  // Computed para controlar o v-model do dialog
  const dialogVisible = computed({
    get: () => props.visible,
    set: (value) => {
      if (!value && props.handleClose) {
        props.handleClose();
      }
    }
  });

  const tabs = [
    { id: 'resumo', label: 'Resumo', icon: 'mdi mdi-layers', tooltip: 'Visão geral da cotação, diagnóstico, fornecedores e pedidos gerados.' },
    { id: 'propostas', label: 'Propostas', icon: 'mdi mdi-handshake', tooltip: 'Comparação de propostas por item ou por fornecedor e definição dos ganhadores.' },
    { id: 'pedidos', label: 'Pedidos', icon: 'mdi mdi-cart-outline', tooltip: 'Pedidos de compra gerados a partir dos itens com decisão de compra nesta cotação.' },
    { id: 'historico', label: 'Histórico', icon: 'mdi mdi-clock', tooltip: 'Linha do tempo de eventos e decisões desta cotação.' }
  ]
  const responderam = computed(() =>
    (resumo.value && resumo.value.metricas && resumo.value.metricas.fornecedores)
      ? resumo.value.metricas.fornecedores.responderam
      : 0
  );

  const fornecedoresResponderamTooltip = computed(() => {
    const total = responderam.value;
    if (total === 0) {
      return { empty: true, message: 'Nenhum fornecedor respondeu até o momento.' };
    }
    const lista = (resumo.value && resumo.value.resumo_fornecedores)
      ? resumo.value.resumo_fornecedores.filter((f) => (f.itens_com_proposta || 0) > 0)
      : [];
    const nomes = lista
      .map((f) => (f.nome_fantasia || f.razao_social || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'));
    const limit = 10;
    const exibir = nomes.slice(0, limit);
    const restante = total > limit ? total - limit : 0;
    return {
      empty: false,
      title: 'Fornecedores que já responderam',
      names: exibir,
      restante
    };
  });

  const steps = computed(() => {
    const status = cotacao.value ? cotacao.value.status_nome : null;
    const r = responderam.value;

    return [
      {
        number: 1,
        label: 'Criada',
        tooltip: 'A cotação foi criada em modo rascunho.',
        completed: !!status, // Se tem status, já foi criada
        active: status === 'Rascunho'
      },
      {
        number: 2,
        label: 'Publicada',
        tooltip: 'A cotação foi publicada e está (ou esteve) disponível para os fornecedores responderem.',
        completed: status !== 'Rascunho' && status !== null,
        active: status === 'Em Cotação'
      },
      {
        number: 3,
        label: 'Propostas recebidas',
        tooltip: 'Já existem propostas registradas para pelo menos um item desta cotação.',
        completed: r > 0,
        active: status === 'Em Cotação' && r > 0
      },
      {
        number: 4,
        label: 'Ganhadores definidos',
        tooltip: 'Todos os itens relevantes têm um ganhador definido ou foram marcados como "não comprar".',
        completed: status === 'Concluída',
        active: status === 'Concluída'
      },
      {
        number: 5,
        label: 'Pedidos gerados',
        tooltip: 'Os pedidos de compra foram gerados para os itens com decisão de compra.',
        completed: cotacao.value && cotacao.value.situacao_pedidos_nome === 'Todos gerados',
        active: cotacao.value && cotacao.value.status_nome === 'Concluída' && cotacao.value.situacao_pedidos_nome !== 'Todos gerados'
      }
    ];
  });
  const clearData = () => {
    cotacao.value = null;
    produtos.value = [];
    resumo.value = {};
    fornecedores.value = [];
    responsavel.value = {};
    config.value = {};
    seguidores.value = [];
    empresa.value = {};
    orders.value = [];
  };

  const fetchDetails = async () => {
    try {
      const { data } = await api.get(`/cotacao/detalhes_cotacao/${props.idCotacao}`);
      if (data.success) {
        cotacao.value = data.cotacao;
        produtos.value = data.produtos;
        fornecedores.value = data.fornecedores;
        resumo.value = data.resumo;
        responsavel.value = data.responsavel;
        config.value = data.config[0];
        seguidores.value = data.seguidores;
        empresa.value = data.empresa_cliente_solicitante;
      }
    } catch (error) {
      toast.error("Erro ao buscar detalhes da cotação!");

    }
  };

  const fetchPedidos = async () => {
    try {
      const { data } = await api.get(`/cotacao/listar_pedidos_cotacao/${props.idCotacao}`);
      pedidos.value = data.pedidos;
    } catch (error) {
      toast.error("Erro ao buscar pedidos!");

    }
  };

  const activeTab = ref(0);

  watch(() => props.visible, async (newVal) => {
    if (newVal) {
      activeTab.value = 0;
      clearData();
      await fetchDetails();
      if (cotacao.value && cotacao.value.situacao_pedidos_nome === 'Todos gerados') {
        await fetchPedidos();
      }
    }
  });

  watch(() => props.idCotacao, async (newId, oldId) => {
    if (newId && newId !== oldId && props.visible) {
      clearData();
      await fetchDetails();
      if (cotacao.value && cotacao.value.situacao_pedidos_nome === 'Todos gerados') {
        await fetchPedidos();
      }
    }
  });

  const getChipColor = (status) => {
    switch (status) {
      case 'Não iniciou': return 'blue';
      case 'Em preenchimento': return 'orange';
      case 'Proposta enviada': return 'success';
      default: return 'grey';
    }
  };

  const getProgressBarColor = (progress) => {
    if (progress === 0) return 'grey';
    if (progress < 50) return 'orange';
    if (progress < 100) return 'blue';
    return 'success';
  };



  const prazoInfo = computed(() => {
    const terminoStr = config.value && config.value.termino_periodo_recebimento_proposta;
    if (!terminoStr) return null;

    const termino = parseDate(terminoStr);
    if (!termino) return null;

    const now = new Date();
    const termDate = new Date(termino);

    // Se a data/hora atual já passou do término, está vencido
    if (now > termDate) {
      return { text: 'Vencido', color: 'error--text' };
    }

    // Para as mensagens amigáveis ("Vence hoje", "amanhã"), comparamos as datas sem a hora
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const termDay = new Date(termDate);
    termDay.setHours(0, 0, 0, 0);

    const diffTime = termDay.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return { text: 'Vence hoje', color: 'warning--text' };
    if (diffDays === 1) return { text: 'Vence amanhã', color: 'warning--text' };

    return { text: `Vence em ${diffDays} dias`, color: 'warning--text' };
  });

  return {
    fornecedoresResponderamTooltip,
    isLoading,
    close,
    edit,
    repeatQuotation,
    endQuotationNow,
    extendDeadlineDialog,
    openExtendDeadlineDialog,
    closeExtendDeadlineDialog,
    fetchDetails,
    dialogVisible,
    tabs,
    steps,
    orders,
    activeTab,
    getStatusColor,
    getStatusIcon,
    getChipColor,
    getDecisaoColor,
    getSituacaoColor,
    getProgressBarColor,
    cotacao,
    produtos,
    resumo,
    fornecedores,
    responsavel,
    config,
    seguidores,
    empresa,
    pedidos,
    prazoInfo,
    baixarEvidencias,
    gerarPedidos,
    baixarPdfPedido
  };
}
